/**
 * Client-side request draft snapshots (#1114) — survives browser refresh before save.
 * Create Request: full formData. Request Detail edit: unsaved overlay + rack session.
 */

import { Platform } from 'react-native';

import { storage } from './storage';

import type { LocationTagDraft } from '../contexts/LocationTagsDraftContext';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';

export const REQUEST_DRAFT_SNAPSHOT_VERSION = 1 as const;
const STORAGE_PREFIX = 'request-draft:';
const MAX_SNAPSHOT_BYTES = 512_000;

/** #1114 — toast after browser refresh hydrates unsaved create/edit work. */
export const REQUEST_DRAFT_RESTORED_MESSAGE =
  'Your in-progress request was restored.';

/** Query flag: explicit New Request — discard stored create snapshot. */
export const FRESH_CREATE_REQUEST_PARAM = 'fresh';

export function isFreshCreateRequestQuery(search: string): boolean {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  if (!normalized) {
    return false;
  }
  return (
    new URLSearchParams(normalized).get(FRESH_CREATE_REQUEST_PARAM) === '1'
  );
}

export function stripFreshCreateRequestParam(
  pathname: string,
  search: string,
): string {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  params.delete(FRESH_CREATE_REQUEST_PARAM);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** True when the page was loaded via browser refresh (not in-app navigation). */
export function isBrowserReloadNavigation(): boolean {
  if (Platform.OS !== 'web' || typeof performance === 'undefined') {
    return false;
  }
  const entry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return entry?.type === 'reload';
}

export type RackSessionSnapshot = {
  baselineRacks: RackConfiguration[];
  sessionRacks: RackConfiguration[];
};

export type SmallUpsSessionSnapshot = {
  baselineSmallUps: SmallUpsConfiguration[];
  sessionSmallUps: SmallUpsConfiguration[];
};

export type LargeUpsSessionSnapshot = {
  baselineLargeUps: LargeUpsConfiguration[];
  sessionLargeUps: LargeUpsConfiguration[];
};

export type CoolingSystemSessionSnapshot = {
  baselineCoolingSystems: CoolingSystemConfiguration[];
  sessionCoolingSystems: CoolingSystemConfiguration[];
};

export type PowerDistributionSessionSnapshot = {
  baselinePowerDistribution: PowerDistributionConfiguration[];
  sessionPowerDistribution: PowerDistributionConfiguration[];
};

export type LiquidCoolingCduSessionSnapshot = {
  baselineLiquidCoolingCdus: LiquidCoolingCduConfiguration[];
  sessionLiquidCoolingCdus: LiquidCoolingCduConfiguration[];
};

export type CreateRequestDraftSnapshot = {
  version: typeof REQUEST_DRAFT_SNAPSHOT_VERSION;
  savedAt: string;
  stepId: string;
  formData: Record<string, unknown>;
  /** #999 — draft location tags added before first save (in-memory context otherwise). */
  locationTagsDraft?: LocationTagDraft[];
};

export type EditRequestDraftSnapshot = {
  version: typeof REQUEST_DRAFT_SNAPSHOT_VERSION;
  savedAt: string;
  requestId: string;
  stepId: string;
  editedData: Record<string, unknown>;
  dirtyFields: string[];
  rackSession?: RackSessionSnapshot;
  smallUpsSession?: SmallUpsSessionSnapshot;
  largeUpsSession?: LargeUpsSessionSnapshot;
  coolingSystemSession?: CoolingSystemSessionSnapshot;
  powerDistributionSession?: PowerDistributionSessionSnapshot;
  liquidCoolingCduSession?: LiquidCoolingCduSessionSnapshot;
  pendingCaptionEdits?: Record<number, string>;
};

function sanitizeUserKey(userEmail: string | null | undefined): string {
  const trimmed = userEmail?.trim().toLowerCase();
  return trimmed && trimmed.length > 0 ? trimmed : 'anonymous';
}

function createDraftKey(userEmail: string | null | undefined): string {
  return `${STORAGE_PREFIX}create:${sanitizeUserKey(userEmail)}`;
}

function editDraftKey(
  userEmail: string | null | undefined,
  requestId: string,
): string {
  return `${STORAGE_PREFIX}edit:${sanitizeUserKey(userEmail)}:${requestId}`;
}

function draftKeyPrefix(userEmail: string | null | undefined): string {
  return `${STORAGE_PREFIX}edit:${sanitizeUserKey(userEmail)}:`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function serializeSnapshot<T>(snapshot: T): string | null {
  try {
    const json = JSON.stringify(snapshot);
    if (json.length > MAX_SNAPSHOT_BYTES) {
      console.warn(
        'Request draft snapshot exceeds size limit; skipping persist',
      );
      return null;
    }
    return json;
  } catch (error) {
    console.warn('Request draft snapshot serialization failed:', error);
    return null;
  }
}

async function writeSnapshot(key: string, snapshot: unknown): Promise<void> {
  const json = serializeSnapshot(snapshot);
  if (!json) {
    return;
  }
  await storage.setItem(key, json);
}

function writeSnapshotSync(key: string, snapshot: unknown): void {
  if (
    Platform.OS !== 'web' ||
    typeof window === 'undefined' ||
    !window.localStorage
  ) {
    return;
  }
  const json = serializeSnapshot(snapshot);
  if (!json) {
    return;
  }
  window.localStorage.setItem(key, json);
}

function parseSnapshot<T>(raw: string | null): T | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as T;
    if (
      !isPlainObject(parsed) ||
      parsed.version !== REQUEST_DRAFT_SNAPSHOT_VERSION
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

const CREATE_IGNORE_KEYS = new Set([
  'requestorName',
  'requestorEmail',
  'requestorPhone',
  'requestorRole',
  'uom',
  'stage',
  'dealLinked',
  'useDefaultSponsors',
]);

/** True when create form has user-entered content worth restoring. */
export function hasCreateDraftContent(
  formData: Record<string, unknown>,
): boolean {
  return Object.entries(formData).some(([key, value]) => {
    if (CREATE_IGNORE_KEYS.has(key)) {
      return false;
    }
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    if (value === false) {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    return true;
  });
}

/** #999 — draft location tags alone are worth restoring, even with an empty form. */
export function hasCreateDraftTags(
  tags: LocationTagDraft[] | null | undefined,
): boolean {
  return Array.isArray(tags) && tags.length > 0;
}

export async function readCreateRequestDraftSnapshot(
  userEmail: string | null | undefined,
): Promise<CreateRequestDraftSnapshot | null> {
  const raw = await storage.getItem(createDraftKey(userEmail));
  return parseSnapshot<CreateRequestDraftSnapshot>(raw);
}

export async function writeCreateRequestDraftSnapshot(
  userEmail: string | null | undefined,
  snapshot: Omit<CreateRequestDraftSnapshot, 'version' | 'savedAt'>,
): Promise<void> {
  await writeSnapshot(createDraftKey(userEmail), {
    version: REQUEST_DRAFT_SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    ...snapshot,
  });
}

export function writeCreateRequestDraftSnapshotSync(
  userEmail: string | null | undefined,
  snapshot: Omit<CreateRequestDraftSnapshot, 'version' | 'savedAt'>,
): void {
  writeSnapshotSync(createDraftKey(userEmail), {
    version: REQUEST_DRAFT_SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    ...snapshot,
  });
}

export async function clearCreateRequestDraftSnapshot(
  userEmail: string | null | undefined,
): Promise<void> {
  await storage.removeItem(createDraftKey(userEmail));
}

export async function readEditRequestDraftSnapshot(
  userEmail: string | null | undefined,
  requestId: string,
): Promise<EditRequestDraftSnapshot | null> {
  const raw = await storage.getItem(editDraftKey(userEmail, requestId));
  const parsed = parseSnapshot<EditRequestDraftSnapshot>(raw);
  if (!parsed || parsed.requestId !== requestId) {
    return null;
  }
  return parsed;
}

export async function writeEditRequestDraftSnapshot(
  userEmail: string | null | undefined,
  snapshot: Omit<EditRequestDraftSnapshot, 'version' | 'savedAt'>,
): Promise<void> {
  await writeSnapshot(editDraftKey(userEmail, snapshot.requestId), {
    version: REQUEST_DRAFT_SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    ...snapshot,
  });
}

export function writeEditRequestDraftSnapshotSync(
  userEmail: string | null | undefined,
  snapshot: Omit<EditRequestDraftSnapshot, 'version' | 'savedAt'>,
): void {
  writeSnapshotSync(editDraftKey(userEmail, snapshot.requestId), {
    version: REQUEST_DRAFT_SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    ...snapshot,
  });
}

export async function clearEditRequestDraftSnapshot(
  userEmail: string | null | undefined,
  requestId: string,
): Promise<void> {
  await storage.removeItem(editDraftKey(userEmail, requestId));
}

export async function clearAllRequestDraftSnapshots(
  userEmail: string | null | undefined,
): Promise<void> {
  await clearCreateRequestDraftSnapshot(userEmail);

  const prefix = draftKeyPrefix(userEmail);
  if (
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    window.localStorage
  ) {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => window.localStorage.removeItem(key));
    return;
  }

  if (Platform.OS !== 'web') {
    try {
      const AsyncStorage =
        require('@react-native-async-storage/async-storage').default;
      const allKeys: string[] = await AsyncStorage.getAllKeys();
      const editKeys = allKeys.filter((key: string) => key.startsWith(prefix));
      if (editKeys.length > 0) {
        await AsyncStorage.multiRemove(editKeys);
      }
    } catch (error) {
      console.warn('Failed to clear edit draft snapshots on native:', error);
    }
  }
}
